import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MessageText, Send, Xmark } from 'iconoir-react';

interface TicketUser {
  id: number;
  name: string;
  email: string;
}

interface SupportTicket {
  id: number;
  user: TicketUser;
  subject: string;
  message: string;
  priority: string;
  status: string;
  category: string;
  created_at: string;
}

interface SupportTicketMessage {
  id: number;
  message: string;
  is_admin: boolean;
  created_at: string;
  user?: TicketUser;
}

interface SupportTicketDetail extends SupportTicket {
  messages: SupportTicketMessage[];
}

const SupportTickets: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailTicketId, setDetailTicketId] = useState<number | null>(null);
  const [detailTicket, setDetailTicket] = useState<SupportTicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const response = await api.get('/admin/support-tickets');
      setTickets(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId: number, status: string) => {
    try {
      await api.put(`/admin/support-tickets/${ticketId}`, { status });
      await loadTickets();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    }
  };

  const loadTicketDetail = async (ticketId: number) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const response = await api.get(`/admin/support-tickets/${ticketId}`);
      setDetailTicket(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du ticket:', error);
      setDetailError('Impossible de charger ce ticket.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openTicketDetail = (ticketId: number) => {
    setDetailTicketId(ticketId);
    setReplyMessage('');
    loadTicketDetail(ticketId);
  };

  const closeTicketDetail = () => {
    setDetailTicketId(null);
    setDetailTicket(null);
    setDetailError('');
    setReplyMessage('');
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || detailTicketId === null || sending) return;
    setSending(true);
    try {
      await api.post(`/admin/support-tickets/${detailTicketId}/messages`, {
        message: replyMessage,
      });
      setReplyMessage('');
      await loadTicketDetail(detailTicketId);
      await loadTickets();
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setDetailError('Erreur lors de l\'envoi de la réponse.');
    } finally {
      setSending(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      low: { label: 'Basse', color: '#95a5a6' },
      medium: { label: 'Moyenne', color: '#3498db' },
      high: { label: 'Haute', color: '#f39c12' },
      urgent: { label: 'Urgente', color: '#e74c3c' },
    };
    return badges[priority] || badges.medium;
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      open: { label: 'Ouvert', color: '#3498db' },
      in_progress: { label: 'En cours', color: '#f39c12' },
      waiting_user: { label: 'En attente utilisateur', color: '#9b59b6' },
      resolved: { label: 'Résolu', color: '#00bf7e' },
      closed: { label: 'Fermé', color: '#95a5a6' },
    };
    return badges[status] || badges.open;
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="support-tickets">
      <div className="section-header">
        <h2>Tickets de support</h2>
        <p>{tickets.length} ticket(s) au total</p>
      </div>

      <div className="tickets-list">
        {tickets.length === 0 ? (
          <div className="empty-state">Aucun ticket de support</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Utilisateur</th>
                <th>Sujet</th>
                <th>Catégorie</th>
                <th>Priorité</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => {
                const priorityBadge = getPriorityBadge(ticket.priority);
                const statusBadge = getStatusBadge(ticket.status);
                return (
                  <tr key={ticket.id}>
                    <td>#{ticket.id}</td>
                    <td>
                      <div>
                        <strong>{ticket.user.name}</strong>
                        <br />
                        <small>{ticket.user.email}</small>
                      </div>
                    </td>
                    <td>
                      <strong>{ticket.subject}</strong>
                      <br />
                      <small>{ticket.message.substring(0, 50)}...</small>
                    </td>
                    <td>{ticket.category}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: priorityBadge.color }}
                      >
                        {priorityBadge.label}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: statusBadge.color }}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <div className="action-buttons">
                        {ticket.status === 'open' && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => updateTicketStatus(ticket.id, 'in_progress')}
                          >
                            Prendre en charge
                          </button>
                        )}
                        {ticket.status === 'in_progress' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                          >
                            Résoudre
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openTicketDetail(ticket.id)}
                          title="Voir la conversation"
                        >
                          <MessageText width={16} height={16} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detailTicketId !== null && (
        <div className="reply-modal">
          <div className="reply-modal-content ticket-detail-modal-content">
            <button className="ticket-detail-close" onClick={closeTicketDetail} title="Fermer">
              <Xmark width={20} height={20} strokeWidth={2} />
            </button>

            {detailLoading && <div>Chargement du ticket...</div>}

            {!detailLoading && detailError && !detailTicket && (
              <div className="settings-error">{detailError}</div>
            )}

            {!detailLoading && detailTicket && (
              <>
                <div className="ticket-detail-header">
                  <h3>Ticket #{detailTicket.id} — {detailTicket.subject}</h3>
                  <p className="ticket-detail-user">
                    {detailTicket.user.name} ({detailTicket.user.email})
                  </p>
                  <div className="ticket-detail-meta">
                    <span className="status-badge" style={{ backgroundColor: getPriorityBadge(detailTicket.priority).color }}>
                      {getPriorityBadge(detailTicket.priority).label}
                    </span>
                    <span className="status-badge" style={{ backgroundColor: getStatusBadge(detailTicket.status).color }}>
                      {getStatusBadge(detailTicket.status).label}
                    </span>
                    <span>{detailTicket.category}</span>
                    <span>{new Date(detailTicket.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                </div>

                <div className="ticket-conversation">
                  <div className="ticket-message ticket-message-user">
                    <div className="ticket-message-author">{detailTicket.user.name}</div>
                    <div className="ticket-message-body">{detailTicket.message}</div>
                    <div className="ticket-message-date">
                      {new Date(detailTicket.created_at).toLocaleString('fr-FR')}
                    </div>
                  </div>

                  {detailTicket.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`ticket-message ${msg.is_admin ? 'ticket-message-admin' : 'ticket-message-user'}`}
                    >
                      <div className="ticket-message-author">
                        {msg.is_admin ? 'Support Ventilio' : (msg.user?.name || detailTicket.user.name)}
                      </div>
                      <div className="ticket-message-body">{msg.message}</div>
                      <div className="ticket-message-date">
                        {new Date(msg.created_at).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  ))}
                </div>

                {detailError && <div className="settings-error">{detailError}</div>}

                <div className="ticket-reply-form">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    className="form-control"
                    rows={4}
                    placeholder="Votre réponse..."
                    disabled={sending}
                  />
                  <div className="modal-actions">
                    <button
                      className="btn btn-primary"
                      onClick={sendReply}
                      disabled={sending || !replyMessage.trim()}
                    >
                      <Send width={18} height={18} strokeWidth={2} />
                      {sending ? 'Envoi...' : 'Envoyer'}
                    </button>
                    <button className="btn btn-secondary" onClick={closeTicketDetail}>
                      Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
